import { useEffect, useState } from "react";
import api from "@/lib/api";
import { DashboardShell } from "@/components/dashboard/DashboardShell";
import { STUDENT_NAV } from "@/pages/student/StudentHome";
import { ReportThread } from "@/components/dashboard/ReportThread";
import { Card } from "@/components/ui/card";
import { SKILLS } from "@/lib/framework";
import { Loader2, ClipboardList, TrendingUp, Target } from "lucide-react";

const RATING = { 1: "Emerging", 2: "Developing", 3: "N/A or Unknown", 4: "Proficient", 5: "Advanced" };
const skillName = (id) => (SKILLS.find((s) => s.id === id) || {}).name || id;

function grouped(report) {
  if (report.skills?.length) {
    return {
      strengths: report.skills.filter((s) => s.kind === "strength"),
      focus: report.skills.filter((s) => s.kind === "focus"),
    };
  }
  return {
    strengths: (report.strengths || []).map((id) => ({ id })),
    focus: (report.focus || []).map((id) => ({ id })),
  };
}

export default function AdvisorUpdates() {
  const [reports, setReports] = useState(null);

  const load = () => api.get("/student/reports").then(({ data }) => setReports(data)).catch(() => setReports([]));
  useEffect(() => {
    load();
    api.post("/student/reports/mark-read").catch(() => {});
  }, []);

  return (
    <DashboardShell title="Advisor Updates" subtitle="Skills updates and comments from your advisors & faculty" nav={STUDENT_NAV}>
      {!reports && <div className="flex h-64 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#003057]" /></div>}
      {reports && reports.length === 0 && <Card className="p-8 text-center text-slate-500" data-testid="no-updates">No skills updates yet. Your advisor's notes will appear here.</Card>}
      <div className="space-y-4">
        {reports && reports.map((r) => {
          const g = grouped(r);
          return (
            <Card key={r.id} className="p-5 card-elevate" data-testid="advisor-update-card">
              <div className="flex items-center justify-between">
                <p className="flex items-center gap-2 font-heading text-base font-bold text-[#003057]"><ClipboardList className="h-5 w-5 text-[#005291]" /> {r.advisor_name}</p>
                <span className="text-xs text-slate-400">{r.created_at?.slice(0, 10)}</span>
              </div>
              <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-slate-700">{r.comment}</p>
              {g.strengths.length > 0 && (
                <div className="mt-3">
                  <p className="flex items-center gap-1.5 text-xs font-bold text-emerald-700"><TrendingUp className="h-3.5 w-3.5" /> Strengths</p>
                  <div className="mt-1 flex flex-wrap gap-1.5">{g.strengths.map((s, i) => <span key={i} className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">{skillName(s.id)}{s.rating ? ` · ${RATING[s.rating]}` : ""}</span>)}</div>
                </div>
              )}
              {g.focus.length > 0 && (
                <div className="mt-2">
                  <p className="flex items-center gap-1.5 text-xs font-bold text-amber-700"><Target className="h-3.5 w-3.5" /> Focus areas</p>
                  <div className="mt-1 flex flex-wrap gap-1.5">{g.focus.map((s, i) => <span key={i} className="rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-700">{skillName(s.id)}{s.rating ? ` · ${RATING[s.rating]}` : ""}</span>)}</div>
                </div>
              )}
              <ReportThread report={r} onPosted={load} />
            </Card>
          );
        })}
      </div>
    </DashboardShell>
  );
}
